import React from "react";
import {
  REVEAL_DURATION_MS,
  graphemes,
  prefersReducedMotion,
  revealedCount,
} from "./draft-reveal.logic";

/**
 * How long to hold the body back while the server answers whether this operator has seen it.
 *
 * Without a hold, the full draft paints and is then replaced by an animation of itself, which
 * looks like a glitch. With an unbounded hold, a slow or failing round trip would hide the draft
 * an operator is waiting to read. So: a short hold, and after it the draft appears regardless —
 * the reveal is a nicety, and the draft is the thing.
 */
const CLAIM_GRACE_MS = 400;

type Phase = "waiting" | "revealing" | "plain";

/**
 * Reveal a newly generated draft once, to the operator it is new to (#804).
 *
 * The animation is not decoration: drafts are produced in the background, so without it an
 * operator opens a request and finds a finished reply that reads as though it had always been
 * there. Showing it arrive is what makes it legible as something the system just did. That is
 * only true once — hence the server-side claim, which is also what stops two tabs from both
 * playing it.
 *
 * `children` is the authoritative body, with evidence highlighting and everything else the panel
 * normally renders. This component never replaces it; it holds it back for at most a second and a
 * half and plays a copy of the text in front of it.
 */
export function DraftReveal({
  draftId,
  body,
  eligible,
  claimFirstView,
  onDisplayed,
  children,
}: {
  /** Identity of the draft being shown. A new one is a new reveal; an edit to this one is not. */
  draftId: string;
  /** The plain text being revealed. */
  body: string;
  /** Whether this presentation is one that may animate at all. */
  eligible: boolean;
  /** Records the view and answers whether this caller is the one that may animate. Absent
   *  where the surrounding screen has no session to claim with, which is itself ineligible. */
  claimFirstView?: (draftId: string) => Promise<boolean>;
  /** Actual visible, fully displayed body; separate from the animation's per-user claim. */
  onDisplayed?: () => void;
  children: React.ReactNode;
}) {
  const [phase, setPhase] = React.useState<Phase>("plain");
  const [shown, setShown] = React.useState(0);
  const parts = React.useMemo(() => graphemes(body), [body]);
  const visibleBody = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    const node = visibleBody.current;
    if (phase !== "plain" || !node || !onDisplayed) return;
    let intersecting = false, recorded = false;
    const record = () => {
      if (!recorded && intersecting && document.visibilityState === "visible"
          && getComputedStyle(node).visibility === "visible") {
        recorded = true;
        onDisplayed();
      }
    };
    const observer = new IntersectionObserver(([entry]) => { intersecting = entry.isIntersecting; record(); });
    observer.observe(node);
    document.addEventListener("visibilitychange", record);
    return () => { observer.disconnect(); document.removeEventListener("visibilitychange", record); };
  }, [phase, draftId, body, onDisplayed]);

  React.useEffect(() => {
    if (!eligible || !claimFirstView || !body) {
      setPhase("plain");
      return;
    }
    let live = true;
    // Reduced motion still records the view: the person saw the draft, and the record is about
    // that, not about whether anything moved.
    const reduced = prefersReducedMotion();
    setPhase(reduced ? "plain" : "waiting");

    let graceTimer: ReturnType<typeof setTimeout> | null = reduced
      ? null
      : setTimeout(() => {
          graceTimer = null;
          // The answer is late. Show the draft; if it turns out we had the first view, it has
          // been spent on a plain render, which is the honest outcome rather than a late jump.
          if (live) setPhase("plain");
        }, CLAIM_GRACE_MS);

    void claimFirstView(draftId)
      .then((claimed) => {
        if (!live) return;
        if (graceTimer === null) return;
        clearTimeout(graceTimer);
        graceTimer = null;
        if (claimed && !reduced) {
          setShown(0);
          setPhase("revealing");
        } else {
          setPhase("plain");
        }
      })
      .catch(() => {
        // A draft an operator cannot read is a far worse failure than one that does not animate.
        if (!live) return;
        if (graceTimer !== null) clearTimeout(graceTimer);
        graceTimer = null;
        setPhase("plain");
      });

    return () => {
      live = false;
      if (graceTimer !== null) clearTimeout(graceTimer);
    };
  }, [draftId, body, eligible, claimFirstView]);

  React.useEffect(() => {
    if (phase !== "revealing") return;
    const total = parts.length;
    if (total === 0) {
      setPhase("plain");
      return;
    }
    const start = Date.now();
    let frame = 0;
    const tick = () => {
      const count = revealedCount(Date.now() - start, total);
      setShown(count);
      if (count >= total) {
        setPhase("plain");
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [phase, parts]);

  if (phase === "revealing") {
    return (
      <>
        {/* The moving copy is decoration; assistive technology is given the finished draft once
            below rather than a stream of single characters. */}
        <span aria-hidden="true" data-testid="draft-reveal-animating">
          {parts.slice(0, shown).join("")}
        </span>
        <span className="sr-only">{body}</span>
      </>
    );
  }

  return (
    <span
      ref={visibleBody}
      data-testid="draft-reveal-body"
      // Held back only while the server is asked, and never longer than CLAIM_GRACE_MS.
      style={phase === "waiting" ? { visibility: "hidden" } : undefined}
    >
      {children}
    </span>
  );
}

export { REVEAL_DURATION_MS };
